import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { fetchDocuments, fetchDocumentsWithFilters, fetchDocumentUrl, uploadDocument, deleteDocument, deleteFolder, uploadFolder, updateFolderUploadProgress, clearFolderUploadState } from './documentsThunks'
import { Document, FolderUploadProgress, FolderUploadResult } from '@/types/document.types'
import { groupDocumentsByFolder } from '@/pages/documents/docHelper'

// Calcula el número máximo de página válido contando unidades visibles
// (cada carpeta cuenta como 1 unidad junto con sus archivos internos,
// los archivos sueltos cuentan como 1). Coincide con el criterio de
// paginación usado en selectPaginatedDocuments.
const computeMaxVisiblePage = (documents: Document[], pageSize: number): number => {
  const { folders, standaloneFiles } = groupDocumentsByFolder(documents)
  const totalUnits = folders.length + standaloneFiles.length
  return Math.max(1, Math.ceil(totalUnits / pageSize))
}

interface DocumentsPaginationState {
  currentPage: number
  pageSize: number
}

interface DocumentsState {
  documents: Document[]
  loadingDocuments: boolean
  errorDocuments: string | null
  downloadUrl: string | null
  isLoadingUrl: boolean
  errorUrl: string | null
  isUploading: boolean
  uploadError: string | null
  uploadSuccess: boolean
  isDeleting: boolean
  deleteError: string | null
  deleteSuccess: boolean
  // Folder upload state
  isFolderUploading: boolean
  folderUploadError: string | null
  folderUploadProgress: FolderUploadProgress | null
  folderUploadResult: FolderUploadResult | null
  pagination: DocumentsPaginationState
}

const initialPagination: DocumentsPaginationState = {
  currentPage: 1,
  pageSize: 10,
}

const initialState: DocumentsState = {
  documents: [],
  loadingDocuments: false,
  errorDocuments: null,
  downloadUrl: null,
  isLoadingUrl: false,
  errorUrl: null,
  isUploading: false,
  uploadError: null,
  uploadSuccess: false,
  isDeleting: false,
  deleteError: null,
  deleteSuccess: false,
  isFolderUploading: false,
  folderUploadError: null,
  folderUploadProgress: null,
  folderUploadResult: null,
  pagination: { ...initialPagination },
}

const documentsSlice = createSlice({
  name: 'documents',
  initialState,
  reducers: {
    clearError: (state) => {
      state.errorDocuments = null
      state.errorUrl = null
    },
    clearDownloadUrl: (state) => {
      state.downloadUrl = null
    },
    setDocumentsPage: (state, action: PayloadAction<number>) => {
      state.pagination.currentPage = action.payload
    },
    setDocumentsPageSize: (state, action: PayloadAction<number>) => {
      state.pagination.pageSize = action.payload
      state.pagination.currentPage = 1
    },
    resetDocumentsPagination: (state) => {
      state.pagination = { ...initialPagination }
    },
  },
  extraReducers: (builder) => {
    // Fetch documents from Firestore
    builder
      .addCase(fetchDocuments.pending, (state) => {
        state.loadingDocuments = true
        state.errorDocuments = null
      })
      .addCase(fetchDocuments.fulfilled, (state, action: PayloadAction<Document[]>) => {
        state.loadingDocuments = false
        ;
        state.documents = action.payload
        state.errorDocuments = null
      })
      .addCase(fetchDocuments.rejected, (state, action: any) => {
        state.loadingDocuments = false
        state.errorDocuments = action.payload || action.error?.message || 'Failed to load documents'
      })
      // Fetch documents with filters from Firestore
      .addCase(fetchDocumentsWithFilters.pending, (state) => {
        state.loadingDocuments = true
        state.errorDocuments = null
        state.pagination = { ...initialPagination }
      })
      .addCase(fetchDocumentsWithFilters.fulfilled, (state, action: PayloadAction<Document[]>) => {
        state.loadingDocuments = false
        state.documents = action.payload
        state.errorDocuments = null
        state.pagination = { ...initialPagination }
      })
      .addCase(fetchDocumentsWithFilters.rejected, (state, action: any) => {
        state.loadingDocuments = false
        state.errorDocuments = action.payload || action.error?.message || 'Failed to load documents with filters'
      })
      // Fetch document URL from Firebase Storage
      .addCase(fetchDocumentUrl.pending, (state) => {
        state.isLoadingUrl = true
        state.errorUrl = null
      })
      .addCase(fetchDocumentUrl.fulfilled, (state, action) => {
        state.isLoadingUrl = false
        state.downloadUrl = action.payload
        state.errorUrl = null
      })
      .addCase(fetchDocumentUrl.rejected, (state, action: any) => {
        state.isLoadingUrl = false
        state.errorUrl = action.payload || action.error?.message || 'Failed to fetch document URL'
      })
      // Upload document handlers
      .addCase(uploadDocument.pending, (state) => {
        state.isUploading = true
        state.uploadError = null
        state.uploadSuccess = false
      })
      .addCase(uploadDocument.fulfilled, (state, action: PayloadAction<any>) => {
        state.isUploading = false
        state.documents.push(action.payload)
        state.uploadSuccess = true
        state.uploadError = null
        // Saltar a página 1 para que el documento recién subido sea alcanzable
        // sin que el usuario tenga que navegar.
        state.pagination.currentPage = 1
      })
      .addCase(uploadDocument.rejected, (state, action: any) => {
        state.isUploading = false
        state.uploadError = action.payload || action.error?.message || 'Failed to upload document'
        state.uploadSuccess = false
      })
      // Delete document handlers
      .addCase(deleteDocument.pending, (state) => {
        state.isDeleting = true
        state.deleteError = null
        state.deleteSuccess = false
      })
      .addCase(deleteDocument.fulfilled, (state, action: PayloadAction<string>) => {
        state.isDeleting = false
        state.documents = state.documents.filter((doc) => doc.id !== action.payload)
        state.deleteSuccess = true
        state.deleteError = null
        // Si tras eliminar la página actual queda fuera de rango, ajustar al
        // último valor válido (contando unidades visibles) para evitar páginas
        // vacías con el usuario "atrapado" navegando hacia atrás.
        const maxPage = computeMaxVisiblePage(state.documents, state.pagination.pageSize)
        if (state.pagination.currentPage > maxPage) {
          state.pagination.currentPage = maxPage
        }
      })
      .addCase(deleteDocument.rejected, (state, action: any) => {
        state.isDeleting = false
        state.deleteError = action.payload || action.error?.message || 'Failed to delete document'
        state.deleteSuccess = false
      })
      // Folder upload progress (standalone action)
      .addCase(updateFolderUploadProgress, (state, action) => {
        if (!state.folderUploadProgress) return
        const { currentFileIndex, status, progress } = action.payload

        if (state.folderUploadProgress.fileStatuses[currentFileIndex]) {
          state.folderUploadProgress.fileStatuses[currentFileIndex].status = status
          state.folderUploadProgress.fileStatuses[currentFileIndex].progress = progress
        }

        state.folderUploadProgress.currentFileIndex = currentFileIndex

        const statuses = state.folderUploadProgress.fileStatuses
        state.folderUploadProgress.completedFiles = statuses.filter(s => s.status === 'success').length
        state.folderUploadProgress.failedFiles = statuses.filter(s => s.status === 'error').length

        const totalProgress = statuses.reduce((sum, s) => sum + s.progress, 0)
        state.folderUploadProgress.overallProgress = Math.round(totalProgress / statuses.length)
      })
      // Clear folder upload state (standalone action)
      .addCase(clearFolderUploadState, (state) => {
        state.isFolderUploading = false
        state.folderUploadError = null
        state.folderUploadProgress = null
        state.folderUploadResult = null
      })
      // Upload folder handlers
      .addCase(uploadFolder.pending, (state, action) => {
        state.isFolderUploading = true
        state.folderUploadError = null
        state.folderUploadResult = null

        const files = action.meta.arg.files
        state.folderUploadProgress = {
          totalFiles: files.length,
          completedFiles: 0,
          failedFiles: 0,
          currentFileIndex: 0,
          overallProgress: 0,
          fileStatuses: files.map(f => ({
            fileName: f.file.name,
            relativePath: f.relativePath,
            status: 'pending' as const,
            progress: 0,
          })),
        }
      })
      .addCase(uploadFolder.fulfilled, (state, action) => {
        state.isFolderUploading = false
        state.folderUploadResult = action.payload
        state.documents.push(...action.payload.successfulUploads)
        if (state.folderUploadProgress) {
          state.folderUploadProgress.overallProgress = 100
        }
        // Saltar a página 1 para que la carpeta recién subida sea alcanzable
        // sin que el usuario tenga que navegar.
        state.pagination.currentPage = 1
      })
      .addCase(uploadFolder.rejected, (state, action: any) => {
        state.isFolderUploading = false
        state.folderUploadError = action.payload || 'Error al subir la carpeta'
      })
      // Delete folder handlers
      .addCase(deleteFolder.pending, (state) => {
        state.isDeleting = true
        state.deleteError = null
        state.deleteSuccess = false
      })
      .addCase(deleteFolder.fulfilled, (state, action: PayloadAction<string[]>) => {
        state.isDeleting = false
        state.documents = state.documents.filter((doc) => !action.payload.includes(doc.id || ''))
        state.deleteSuccess = true
        state.deleteError = null
        // Clamp de página si tras eliminar la carpeta la página actual queda
        // fuera de rango (mismo criterio que deleteDocument).
        const maxPage = computeMaxVisiblePage(state.documents, state.pagination.pageSize)
        if (state.pagination.currentPage > maxPage) {
          state.pagination.currentPage = maxPage
        }
      })
      .addCase(deleteFolder.rejected, (state, action: any) => {
        state.isDeleting = false
        state.deleteError = action.payload || action.error?.message || 'Failed to delete folder'
        state.deleteSuccess = false
      })
  },
})

export const {
  clearError,
  clearDownloadUrl,
  setDocumentsPage,
  setDocumentsPageSize,
  resetDocumentsPagination,
} = documentsSlice.actions

export default documentsSlice.reducer

